/* eslint-disable @nx/enforce-module-boundaries */
import { IconButton, useToast } from '@chakra-ui/react';
import {
  HandShakeSimpleIcon,
  ThreeDot,
  FollowIcon,
  FollowingIconV2,
} from '@mybridge/icons';

import {
  NotIntrestedSadSmileyIcon,
  BlockAndReportIcon,
  DeletePostIcon,
  EditPostIcon,
  PinPostIcon
} from '@mybridge/icons/DashboardNewICons';

import { useDisclosure } from '@mybridge/ui/hooks';
import { Menu, MenuButton, MenuItem, MenuList } from '@mybridge/ui/menu';
import { useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PostBoxContext } from '../context';
import { Spinner } from '@mybridge/ui/spinner';
import { Portal } from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';
import { followCompanyPage, unFollowCompanyPage } from 'v4/store/actions/companyPages.actions';
import { getAllConnectionIDs } from 'v4/store/actions/connection.actions';
import { MutualConnectionIcon } from '@mybridge/icons/MutualConnectionIcon';
import { MyNetworkContext } from 'v4/components/network/context'; // Add this import

export const PostActionsMenu = () => {
  const {
    post,
    notInterestedModal,
    blockReportModal,
    pinPostModal,
    handleFollow,
    following,
    followLoading,
    handleHandshake,
    handshaked,
    handshakeLoading,
    inCircle,
    deletePostModal,
    pinned,
    editPostModal,
    loggedIn,
  } = useContext(PostBoxContext);

  // Add MyNetworkContext for user follow functionality
  const {
    unfollowUser,
    isFollowed,
    followUser,
  } = useContext(MyNetworkContext);

  const { allConnectionIDs } = useSelector((state) => state.connection);
  const { author, company, metadata } = post ?? {};
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const menuDisc = useDisclosure();
  const { t } = useTranslator();
  const router = useRouter();
  const dispatch = useDispatch();
  const openAuthModalDisc = useDisclosure();
  const toast = useToast({ position: 'top' });

  const isCompanyPost = !!company?.name?.length;

  const handleUnFollowPage = () => {
    dispatch(unFollowCompanyPage(company?.id)).then((res) => {
      if (res) {
        toast({
          title: `${t('pages.unfollowedSuccess') || "You have now unfollowed"} ${company?.name}`,
          status: 'success',
          isClosable: true,
          position: 'top',
        });
        dispatch(getAllConnectionIDs());
      }
    });
  };

  const handleFollowPage = () => {
    if (loggedIn) {
      dispatch(followCompanyPage(company?.id)).then((res) => {
        if (res) {
          toast({
            title: `${t('pages.followingSuccess') || "You are now following"} ${company?.name}`,
            status: 'success',
            isClosable: true,
            position: 'top',
          });
          dispatch(getAllConnectionIDs());
        }
      });
    } else {
      openAuthModalDisc.onOpen();
    }
  };

  // Add user follow handlers using the same logic as PublicProfileMenu
  const handleFollowUser = () => {
    if (loggedIn) {
      followUser(author);
    } else {
      openAuthModalDisc.onOpen();
    }
  };

  const handleUnfollowUser = () => {
    unfollowUser(author);
  };

  const postActions = useMemo(
    () =>
      userProfileInfo?.id === author?.id
        ? [
            {
              title: t('postActionMenu.deletePost') || 'Delete Post',
              icon: <DeletePostIcon width={20} height={20} />,
              onClick: () => {
                deletePostModal?.onOpen?.();
                menuDisc.onClose();
              },
            },
            ...(!metadata?.is_default_welcome_post
              ? [
                  {
                    title: t('postActionMenu.editPost') || 'Edit Post',
                    icon: <EditPostIcon width={20} height={20} />,
                    onClick: () => {
                      editPostModal?.onOpen?.();
                      menuDisc.onClose();
                    },
                  },
                ]
              : []),
            {
              title: pinned
                ? t('postActionMenu.unpinFromProfile') || 'Unpin from myProfile'
                : t('postActionMenu.pinToProfile') || 'Pin to myProfile',
              icon: <PinPostIcon width={20} height={20} />,
              onClick: () => {
                pinPostModal?.onOpen?.();
                menuDisc.onClose();
              },
            },
          ]
        : [
            {
              title:
                t('postActionMenu.notInterested') ||
                'Not interested in this post',
              icon: <NotIntrestedSadSmileyIcon width={20} height={20} />,
              onClick: () => {
                notInterestedModal.onOpen();
                menuDisc?.onClose?.();
              },
            },
            // For company posts, show follow/unfollow option
            ...(isCompanyPost
              ? [
                  {
                    title: allConnectionIDs?.company_following_ids?.includes(
                      company?.id
                    )
                      ? t('publicProfile.unfollow') || 'Unfollow'
                      : t('profileNetwork.follow') || 'Follow',
                    icon: followLoading ? (
                      <Spinner size="xs" />
                    ) : allConnectionIDs?.company_following_ids?.includes(
                        company?.id
                      ) ? (
                      <FollowingIconV2 width={20} height={20} />
                    ) : (
                      <FollowIcon width={20} height={20} />
                    ),
                    onClick: () => {
                      allConnectionIDs?.company_following_ids?.includes(
                        company?.id
                      )
                        ? handleUnFollowPage()
                        : handleFollowPage();
                      menuDisc.onClose();
                    },
                  },
                ]
              : [
                  // For user posts, add follow/unfollow user option
                  {
                    title: isFollowed(author)
                      ? `${t('publicProfile.unfollow') || 'Unfollow'} ${
                          author?.first_name || ''
                        }`
                      : `${t('profileNetwork.follow') || 'Follow'} ${
                          author?.first_name || ''
                        }`,
                    icon: <FollowIcon width="20" height="20" color="#868585" />,
                    onClick: () => {
                      isFollowed(author)
                        ? handleUnfollowUser()
                        : handleFollowUser();
                      menuDisc.onClose();
                    },
                  },
                  // Keep handshake option for user connections
                  ...(author?.field_audience_visibility
                    ?.WHO_CAN_SEND_FRIEND_REQ === 1 || inCircle
                    ? [
                        {
                          title: inCircle
                            ? t('postActionMenu.mutualConnection') ||
                              'Mutual Connection'
                            : handshaked
                            ? t('postActionMenu.withdrawHandshake') ||
                              'Withdraw handshake'
                            : t('postActionMenu.handshakeRequest') ||
                              'Handshake Request',
                          icon: inCircle ? (
                            <MutualConnectionIcon width={20} height={20} />
                          ) : handshakeLoading ? (
                            <Spinner size="xs" />
                          ) : (
                            <HandShakeSimpleIcon width={20} height={20} />
                          ),
                          onClick: () => {
                            inCircle
                              ? handleHandshake('Mutual Connection')
                              : handleHandshake();
                            menuDisc.onClose();
                          },
                        },
                      ]
                    : []),
                ]),
            {
              title:
                t('postActionMenu.blockAndReport') || 'Block and Report Post',
              icon: <BlockAndReportIcon width={20} height={20} />,
              onClick: () => {
                blockReportModal.onOpen();
                menuDisc?.onClose?.();
              },
            },
          ],
    [
      author,
      userProfileInfo,
      handleFollow,
      following,
      followLoading,
      handleHandshake,
      handshaked,
      handshakeLoading,
      inCircle,
      isCompanyPost,
      company,
      isFollowed, // Add this dependency
      followUser, // Add this dependency
      unfollowUser, // Add this dependency
    ]
  );

  const isOwnPublicProfile = router.pathname.includes('/p/') && 
                            router.asPath.split('/').pop() === userProfileInfo?.slug;
  
  if (isOwnPublicProfile) {
    return null;
  }

  return (
    <>
      <Menu {...menuDisc} closeOnSelect={false} placement="bottom-end">
        <MenuButton as={IconButton} aria-label={t('postActionMenu.options') || 'Post options'} size={["sm","xs"]} variant={["transparent","ghost"]}>
          <ThreeDot />
        </MenuButton>
        {loggedIn && (
          <Portal>
            <MenuList zIndex="popover">
              {postActions.map?.((action, index) => (
                <MenuItem key={index} icon={action.icon} onClick={action.onClick}>
                  {action.title}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        )}
      </Menu>
    </>
  );
};
